import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Select
} from '@chakra-ui/react'
import type { InsuranceParamsPropertyPagedQuery } from 'BrokerTsLib/src/dtos'
import type { OfferStatusEnum } from 'BrokerTsLib/src/enums/offerStatusEnum'
import { FormProvider, useForm } from 'BrokerTsLib/src/rhf'
import { formatDate } from 'BrokerTsLib/src/Utils/formatPrice'
import { useContext } from 'react'

import { Product, RiskVariant } from 'BrokerTsLib/src/enums'
import { Card, CardBody } from '../../../components/Card'
import { Datepicker } from '../../../components/Datepicker'
import { InputGroup } from '../../../components/InputGroup'
import { Loading } from '../../../components/Loading'
import { useInsurers } from '../../../hooks'
import { useApp } from '../../../hooks/useApp'
import { useGetOrganizationUnits, useGetUsers } from '../../../hooks/useUser'
import { Search } from '../../../icons'
import { conceptsPropertyContext } from '../../../pages/chaloupka/concepts'
import { contractsPropertyContext } from '../../../pages/chaloupka/contracts'
import { SearchWrapper } from '../SearchWrapper'

export interface ConceptsPropertySearchFormValues {
  offerNumber: string
  dateFrom: Date
  dateTo: Date
  insuranceCompany: string
  spz: string
  vin: string
  insureeName: string
  insureeNumber: string
  organization: string
  user: string
  offerStatus: OfferStatusEnum
}

interface ConceptsPropertySearchFormProps {
  onSubmit: (search: InsuranceParamsPropertyPagedQuery) => void,
  filter: InsuranceParamsPropertyPagedQuery,
  listType: 'contracted' | 'opened'
}

export const PropertySearchForm = ({ onSubmit, filter, listType }: ConceptsPropertySearchFormProps) => {
  const { setCurrentPage, loading: loadingList } = useContext(listType == 'opened' ? conceptsPropertyContext : contractsPropertyContext)
  const { app } = useApp()

  const methods = useForm<InsuranceParamsPropertyPagedQuery>({
    defaultValues: filter,
  })

  const { register, getValues, setValue, handleSubmit, formState: { isSubmitting } } = methods

  const { data: allInsurers, isFetched: isFetchedInsurers } = useInsurers(null, listType == 'contracted')
  const { data: organizationUnits, isFetched: isFetchedOrganizationUnits } = useGetOrganizationUnits()
  const { data: users, isFetched: isFetchedUsers } = useGetUsers()

  const loading = !isFetchedInsurers || !isFetchedUsers || !isFetchedOrganizationUnits

  const handleSelectProduct = (product: Product | null, variant: RiskVariant | null) => {
    setValue('product', product)
    setValue('type', variant)
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        {loading &&
          <Card>
            <CardBody>
              <Loading large={true} />
            </CardBody>
          </Card>
        }

        {!loading &&
          <SearchWrapper>
            <FormControl variant="fullWidth">
              <FormLabel htmlFor="offerNumber">Číslo nabídky</FormLabel>

              <InputGroup id="offerNumber" placeholder="Např: 73622" {...register('insuranceParamsId')} />
            </FormControl>

            {listType == 'contracted' &&
              <>
                <FormControl variant="fullWidth">
                  <FormLabel htmlFor="contractNumber">Číslo smlouvy</FormLabel>

                  <InputGroup id="contractNumber" placeholder="Např: 73622" {...register('contractNumber')} />
                </FormControl>

                <FormControl variant="fullWidth">
                  <FormLabel htmlFor="dateFrom">Datum sjednání od</FormLabel>

                  <Datepicker
                    date={getValues('dateContractedFrom')}
                    onDateChange={(date) => setValue('dateContractedFrom', formatDate(date))}
                  />
                </FormControl>

                <FormControl variant="fullWidth">
                  <FormLabel htmlFor="dateTo">Datum sjednání do</FormLabel>

                  <Datepicker
                    date={getValues('dateContractedTo')}
                    onDateChange={(date) => setValue('dateContractedTo', formatDate(date))}
                  />
                </FormControl>

                <FormControl variant="fullWidth">
                  <FormLabel>Datum výročí od</FormLabel>

                  <Datepicker
                    date={getValues('anniversaryFrom')}
                    onDateChange={(date) => setValue('anniversaryFrom', formatDate(date))}
                  />
                </FormControl>

                <FormControl variant="fullWidth">
                  <FormLabel>Datum výročí do</FormLabel>

                  <Datepicker
                    date={getValues('anniversaryTo')}
                    onDateChange={(date) => setValue('anniversaryTo', formatDate(date))}
                  />
                </FormControl>
              </>
            }

            {listType == 'opened' &&
              <>
                <FormControl variant="fullWidth">
                  <FormLabel htmlFor="dateFrom">Datum od</FormLabel>

                  <Datepicker
                    date={getValues('dateCreatedFrom')}
                    onDateChange={(date) => setValue('dateCreatedFrom', formatDate(date))}
                  />
                </FormControl>

                <FormControl variant="fullWidth">
                  <FormLabel htmlFor="dateTo">Datum do</FormLabel>

                  <Datepicker
                    date={getValues('dateCreatedTo')}
                    onDateChange={(date) => setValue('dateCreatedTo', formatDate(date))}
                  />
                </FormControl>
              </>
            }

            {listType == 'contracted' && app != 'chaloupka' &&
              <>
                <FormControl variant="fullWidth">
                  <FormLabel>Datum výročí od</FormLabel>

                  <Datepicker
                    date={getValues('anniversaryFrom')}
                    onDateChange={(date) => setValue('anniversaryFrom', formatDate(date))}
                  />
                </FormControl>

                <FormControl variant="fullWidth">
                  <FormLabel>Datum výročí do</FormLabel>

                  <Datepicker
                    date={getValues('anniversaryTo')}
                    onDateChange={(date) => setValue('anniversaryTo', formatDate(date))}
                  />
                </FormControl>
              </>
            }

            {(allInsurers?.length ?? 0) > 0 &&
              <FormControl variant="fullWidth">
                <FormLabel htmlFor="insuranceCompany">Pojišťovna</FormLabel>

                <Select {...register('insurerId')}>
                  <option value=''>
                    Vyberte
                  </option>
                  {allInsurers?.map((company) => {
                    return (
                      <option key={company.id} value={company.id.toString()}>
                        {company.description}
                      </option>
                    )
                  })}
                </Select>
              </FormControl>
            }

            <FormControl variant="fullWidth">
              <FormLabel htmlFor="insureeName">Pojistník (příjmení/název firmy)</FormLabel>

              <InputGroup id="insureeName" placeholder="Např: Novák" {...register('legalPersonName')} />
            </FormControl>

            <FormControl variant="fullWidth">
              <FormLabel htmlFor="insureeNumber">Pojistník (rodné číslo/IČO)</FormLabel>

              <InputGroup
                id="insureeNumber"
                placeholder="Např: 9001012233"
                {...register('legalPersonIdentification')}
              />
            </FormControl>

            {(organizationUnits?.data.length ?? 0) > 1 &&
              <FormControl variant="fullWidth">
                <FormLabel htmlFor="organization">Organizační jednotka</FormLabel>

                <Select {...register('ouId')}>
                  <option value=''>
                    Vyberte organizaci
                  </option>
                  {organizationUnits?.data.map((company) => {
                    return (
                      <option key={company.id} value={company.id.toString()}>
                        {company.name}
                      </option>
                    )
                  })}
                </Select>
              </FormControl>
            }

            {(users?.data.length ?? 0) > 1 &&
              <FormControl variant="fullWidth">
                <FormLabel htmlFor="user">Uživatel</FormLabel>

                <Select {...register('userId')}>
                  <option value=''>
                    Vyberte uživatele
                  </option>
                  {users?.data.map((user) => {
                    return (
                      <option key={user.userId} value={user.userId}>
                        {user.surname} {user.name} ({organizationUnits?.data.find(x => x.id.toString() === user.ouId?.toString())?.name})
                      </option>
                    )
                  })}
                </Select>
              </FormControl>
            }

            <FormControl variant="fullWidth">
              <FormLabel htmlFor="user">Předmět pojištění</FormLabel>

              <Select
                onChange={(e) => {
                  const value = e.target.value

                  switch (value) {
                    case 'realty':
                      handleSelectProduct(Product.PropertyInsurance, RiskVariant.Realty)
                      break
                    case 'household':
                      handleSelectProduct(Product.PropertyInsurance, RiskVariant.Household)
                      break
                    case 'both':
                      handleSelectProduct(Product.PropertyInsurance, RiskVariant.HouseholdAndRealty)
                      break
                    case 'liability':
                      handleSelectProduct(Product.LiabilityInsurance, null)
                      break
                    default: handleSelectProduct(null, null); break
                  }
                }}
              >
                <option value="">Vyberte předmět pojitění</option>
                <option value="realty">Nemovitost</option>
                <option value="household">Domácnost</option>
                <option value="both">Nemovitost + Domácnost</option>
                <option value="liability">Samostatná odpovědnost</option>
              </Select>
            </FormControl>

            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                columnGap: 4,
                rowGap: 4,
                alignItems: 'end',
                justifyContent: 'end',
                gridColumn: { base: '', md: '3 / 5' },
              }}
            >
              <Button type="submit" colorScheme="primary" onClick={() => setCurrentPage(1)} isLoading={isSubmitting || loadingList}>
                <Search />
                Vyhledat
              </Button>
            </Box>
          </SearchWrapper>
        }
      </form>
    </FormProvider>
  )
}
